/*
 * InputField.tsx
 * Reusable input field component with label, error handling, and custom styling.
 */
import React from 'react';
import { Skeleton } from '@radix-ui/themes';
import '../../styles/global.css';

// Props definition
interface InputFieldProps {
	label: string;
	name: string;
	type?: string;
	value: string | number | undefined;
	onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	error?: string | null;
	placeholder?: string;
	required?: boolean;
	visibility?: boolean;
	readOnly?: boolean;
	loading?: boolean;
}

// InputField component
const InputField: React.FC<InputFieldProps> = ({
	label,
	name,
	type = 'text',
	value,
	onChange,
	error,
	placeholder,
	required,
	visibility,
	readOnly,
	loading,
}) => {
	// Génération d'un identifiant unique pour l'input
	const inputId = `input-${name}`; // unique pour chaque champ
	// Gestion de la visibilité à true si non définie
	const visibilityInput = visibility ?? true;
	// Gestion de la propriété readOnly
	const readOnlyInput = readOnly ?? false;

	// Rendu du composant
	return (
		<div style={{ display: visibilityInput ? 'block' : 'none' }}>
			<label
				htmlFor={inputId}
				className="block text-sm font-medium text-gray-700 mb-1"
			>
				{label}
			</label>
			{loading ? (
				<Skeleton
					className="w-full h-10 rounded block bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200"
					style={{
						backgroundSize: '200% 100%',
						animation: 'shimmer 1.5s infinite linear',
					}}
				/>
			) : (
				<input
					id={inputId}
					name={name}
					placeholder={placeholder}
					type={type}
					value={value || ''}
					onChange={onChange}
					className={`
					w-full h-10 border p-2 rounded bg-gray-100
					focus:outline-none focus:ring-2 focus:ring-blue-500
					placeholder:italic
					${readOnlyInput ? 'text-gray-400 cursor-not-allowed bg-gray-200' : ''}`}
					required={required}
					readOnly={readOnlyInput}
				/>
			)}
			{error && <p className="text-red-600 text-sm mt-1">{error}</p>}
		</div>
	);
};

// Export the component
export default InputField;
